/**
 * AuthContext – centralizovani auth state i akcije (login, logout, setUser).
 * Preseca prop drilling; komponente koriste useAuth() umesto user/onLogin/onLogout props.
 */
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import AuthService from '@/core/auth/AuthService';
import { clearLoginDataCache, setCachedLoginData } from '@/core/api/ApiService';
import toast from 'react-hot-toast';
import UserPreferencesService from '@/shared/services/UserPreferencesService';
import {
  getSelectedLegalSubjectId,
  isTeamLeadForOrganization,
} from '@/shared/auth/teamLeadCapabilities';
import { isPlatformAdmin } from '@/shared/auth/obligationAuth';

const AuthContext = createContext(null);

/**
 * Pokušava da dohvati UUID naloga ako ga /login profil ne sadrži.
 * BE TODO: /login treba da vrati profile.id sa UUID nalogom.
 * FE workaround: POST /account/search po emailu korisnika.
 */
async function resolveAccountIdFallback(email) {
  if (!email) return null;
  try {
    const ApiService = (await import('@/core/api/ApiService')).default;
    const { API_ENDPOINTS } = await import('@/core/api/endpoints');
    const res = await ApiService.post(API_ENDPOINTS.ACCOUNT.SEARCH, { search: email });
    const users = res?.data?.users ?? res?.data?.data?.users ?? (Array.isArray(res?.data) ? res.data : []);
    const match = users.find((u) => {
      const uEmail = u.email ?? u.sub ?? '';
      return uEmail.toLowerCase() === email.toLowerCase();
    });
    const uuid = match?.id ?? match?.account_id ?? null;
    return uuid && !String(uuid).includes('@') ? uuid : null;
  } catch {
    return null;
  }
}

async function loadUserWithLoginData(u) {
  if (!u || u.loginData) return u;
  try {
    const AccountService = (await import('@/shared/services/AccountService')).default;
    const loginResponse = await AccountService.checkLogin();
    if (!loginResponse || !loginResponse.profile) {
      clearLoginDataCache();
      await AuthService.removeUser();
      return null;
    }
    const profile = loginResponse.profile || {};

    // BE workaround: ako /login ne vrati profile.id, pokušaj da ga dohvatimo
    // Ukloniti kada BE doda id u /login response
    if (!profile.id) {
      const fallbackId = await resolveAccountIdFallback(profile.email);
      if (fallbackId) profile.id = fallbackId;
    }

    u.loginData = {
      profile,
      roles: loginResponse.roles || [],
      organizations: loginResponse.organizations || { requested: [], verified: [] },
    };
    setCachedLoginData(u.access_token, u.loginData);
    return u;
  } catch {
    clearLoginDataCache();
    await AuthService.removeUser();
    return null;
  }
}

function isTokenExpired(user) {
  if (!user?.expires_at) return false;
  const now = Math.floor(Date.now() / 1000);
  return user.expires_at <= now;
}

/**
 * Vraća true kada je oidc-client renewal odbijen od strane provajdera jer SSO sesija
 * zahteva interakciju korisnika (login_required, interaction_required, consent_required).
 * U tom slučaju silent renew nije moguć — korisnik mora da se ručno prijavi.
 */
function isSsoSessionExpired(error) {
  if (!error) return false;
  const code = String(error.error ?? '').toLowerCase();
  const msg = String(error.message ?? '').toLowerCase();
  return (
    code === 'login_required' ||
    code === 'interaction_required' ||
    code === 'consent_required' ||
    msg.includes('login_required') ||
    msg.includes('interaction_required')
  );
}

/**
 * Provider za globalni auth state (login, logout, token refresh, capabilities).
 * @param {{ children: import('react').ReactNode }} props
 */
export function AuthProvider({ children }) {
  const [user, setUserState] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const setUser = useCallback((value) => {
    setUserState(value);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadUser = async () => {
      try {
        const u = await AuthService.getUser();
        if (cancelled) return;
        if (!u) {
          setUserState(null);
          setIsLoading(false);
          return;
        }
        if (isTokenExpired(u)) {
          try {
            const renewed = await AuthService.renewToken();
            const enriched = await loadUserWithLoginData(renewed);
            if (cancelled) return;
            if (enriched) {
              setUserState(enriched);
            } else {
              clearLoginDataCache();
              await AuthService.removeUser();
              setUserState(null);
            }
          } catch {
            clearLoginDataCache();
            await AuthService.removeUser();
            if (cancelled) return;
            setUserState(null);
          }
          return;
        }
        const enriched = await loadUserWithLoginData(u);
        if (cancelled) return;
        setUserState(enriched);
      } catch {
        if (!cancelled) setUserState(null);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    loadUser();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const handleSessionExpired = (event) => {
      setUserState(null);
      const detail = event?.detail || {};
      const isNetworkError = detail.isNetworkError || false;
      let msg = 'Ваша сесија је истекла. Молимо пријавите се поново.';
      if (isNetworkError) msg = 'Проблем са мрежом приликом обнове сесије. Молимо пријавите се поново.';
      toast.error(msg, { id: 'session-expired', duration: 6000, position: 'bottom-center' });
    };
    window.addEventListener('sessionExpired', handleSessionExpired);
    return () => window.removeEventListener('sessionExpired', handleSessionExpired);
  }, []);

  useEffect(() => {
    if (!user) return;
    const checkTokenExpiry = async () => {
      try {
        const currentUser = await AuthService.getUser();
        if (!currentUser?.expires_at) return;
        const now = Math.floor(Date.now() / 1000);
        const expiresIn = currentUser.expires_at - now;
        if (expiresIn < 300 && expiresIn > 0) {
          try {
            const renewed = await AuthService.renewToken();
            // Učitaj loginData nakon osvežavanja tokena
            const enriched = await loadUserWithLoginData(renewed);
            if (enriched) {
              setUserState(enriched);
            } else {
              // Ako loginData ne može da se učita, sesija je verovatno istekla
              clearLoginDataCache();
              await AuthService.removeUser();
              setUserState(null);
              toast.error('Ваша сесија је истекла. Молимо пријавите се поново.', { id: 'session-expired', duration: 6000, position: 'bottom-center' });
            }
          } catch (renewError) {
            const msg = (renewError?.message || '').toLowerCase();
            const code = String(renewError?.code ?? renewError?.error ?? '').toLowerCase();
            const isNetwork = msg.includes('network') || msg.includes('timeout') || msg.includes('econnaborted') || msg.includes('failed to fetch') || code === 'econnaborted' || code === 'timeout' || renewError?.name === 'NetworkError';
            try {
              clearLoginDataCache();
              await AuthService.removeUser();
            } catch {}
            setUserState(null);
            if (isSsoSessionExpired(renewError)) {
              toast.error('Сесија је истекла. Бићете преусмерени на пријаву...', { id: 'session-expired', duration: 3000, position: 'bottom-center' });
              setTimeout(() => AuthService.login(), 2500);
            } else {
              toast.error(isNetwork ? 'Проблем са мрежом приликом обнове сесије. Молимо пријавите се поново.' : 'Ваша сесија је истекла. Молимо пријавите се поново.', { id: 'session-expired', duration: 6000, position: 'bottom-center' });
            }
          }
        } else if (expiresIn <= 0) {
          let expiredRenewError = null;
          try {
            const renewed = await AuthService.renewToken();
            const enriched = await loadUserWithLoginData(renewed);
            if (enriched) {
              setUserState(enriched);
              return;
            }
          } catch (e) {
            expiredRenewError = e;
          }
          try {
            clearLoginDataCache();
            await AuthService.removeUser();
          } catch {}
          setUserState(null);
          if (isSsoSessionExpired(expiredRenewError)) {
            toast.error('Сесија је истекла. Бићете преусмерени на пријаву...', { id: 'session-expired', duration: 3000, position: 'bottom-center' });
            setTimeout(() => AuthService.login(), 2500);
          } else {
            toast.error('Ваша сесија је истекла. Молимо пријавите се поново.', { id: 'session-expired', duration: 6000, position: 'bottom-center' });
          }
        }
      } catch {}
    };
    checkTokenExpiry();
    const interval = setInterval(checkTokenExpiry, 60000);
    const onVisibility = () => { if (document.visibilityState === 'visible') checkTokenExpiry(); };
    const onFocus = () => checkTokenExpiry();
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
    };
  }, [user]);

  const login = useCallback(async () => {
    try {
      const existingUser = await AuthService.getUser();
      if (existingUser) {
        const expiresAt = existingUser.expires_at;
        const now = Math.floor(Date.now() / 1000);
        const isValid = existingUser.access_token && expiresAt && expiresAt > now;
        if (!isValid) {
          try {
            clearLoginDataCache();
            await AuthService.removeUser();
            setUserState(null);
          } catch {
            try {
              for (let i = 0; i < sessionStorage.length; i++) {
                const key = sessionStorage.key(i);
                if (key && (key.includes('oidc') || key.includes('Oidc'))) sessionStorage.removeItem(key);
              }
              setUserState(null);
            } catch {}
          }
        } else {
          // Učitaj loginData pre postavljanja korisnika
          const enriched = await loadUserWithLoginData(existingUser);
          if (enriched) {
            setUserState(enriched);
          } else {
            setUserState(null);
          }
          return;
        }
      }
      await new Promise((r) => setTimeout(r, 100));
      AuthService.login();
    } catch {
      AuthService.login();
    }
  }, []);

  const logout = useCallback(() => {
    AuthService.logout();
    setUserState(null);
  }, []);

  const value = useMemo(
    () => {
      const preferences = UserPreferencesService.getPreferences();
      const selectedOrganizationId = getSelectedLegalSubjectId(preferences);
      const isTeamLead = isTeamLeadForOrganization(user, selectedOrganizationId);
      return {
        user,
        isLoading,
        login,
        logout,
        setUser,
        capabilities: {
          isAdmin: isPlatformAdmin(user),
          isTeamLead,
          selectedOrganizationId,
        },
      };
    },
    [user, isLoading, login, logout, setUser]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Čita auth kontekst: `user`, `isLoading`, `login`, `logout`, `setUser`, `capabilities`.
 * @returns {{ user: object|null, isLoading: boolean, login: () => Promise<void>, logout: () => void, setUser: (user: object|null) => void, capabilities: { isAdmin: boolean, isTeamLead: boolean, selectedOrganizationId: string|number|null } }}
 */
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
